import { useState } from "react";
import type { ProfileModules } from "@/lib/profileModules";
import { activeRailCount, shouldRenderRail, MODULE_ADVICE, MODULE_ENABLE_FLAG, MODULE_KIND } from "@/lib/profileModules";
import type { ModuleState } from "@/lib/profileModules";

/** Why a module is hidden: an explicit setting, publication/approval, or content readiness. */
const gateFor = (r: ModuleState): string => {
  if (r.active) return "Visible";
  if (r.key === "publicSupporters" && r.missing.includes("supporter list still loading")) return "Permissions / still loading";
  if (MODULE_ENABLE_FLAG[r.key] && r.missing.some((m) => m.startsWith("support_enabled"))) return "Disabled setting";
  if (["causes", "brandRelationships", "relatedAthletes"].includes(r.key)) return "Publication / approval";
  return "Content readiness";
};

/**
 * Admin-only module diagnostic panel. Never rendered for fans, athletes or
 * signed-out visitors, and it never prints payment URLs, tokens or any other
 * internal configuration value — only module state and missing field names.
 */
const ModuleDiagnostics = ({
  modules,
  detailed = false,
  defaultOpen = false,
}: {
  modules: ProfileModules;
  /** Adds enable-state, gate and recommended-action columns for the admin preview tool. */
  detailed?: boolean;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const rows = Object.values(modules);
  const activeCount = rows.filter((r) => r.active).length;

  return (
    <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/40 p-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center justify-between gap-3 text-left text-sm font-semibold"
      >
        <span>
          Admin preview · module diagnostics
          <span className="ml-2 font-normal text-muted-foreground">
            {activeCount}/{rows.length} active · rail {shouldRenderRail(modules) ? `on (${activeRailCount(modules)})` : `off (${activeRailCount(modules)})`}
          </span>
        </span>
        <span aria-hidden>{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-4 font-semibold">Module</th>
                <th className="py-2 pr-4 font-semibold">State</th>
                {detailed && <th className="py-2 pr-4 font-semibold">Enabled setting</th>}
                <th className="py-2 pr-4 font-semibold">Reason</th>
                <th className="py-2 pr-4 font-semibold">Missing</th>
                {detailed && <th className="py-2 pr-4 font-semibold">Hidden because</th>}
                {detailed && <th className="py-2 font-semibold">Recommended action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.key} className="align-top">
                  <td className="py-2 pr-4 font-semibold">{r.label}</td>
                  <td className="py-2 pr-4">
                    <span className={r.active ? "text-primary font-semibold" : "text-muted-foreground"}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {detailed && (
                    <td className="py-2 pr-4 text-muted-foreground break-words">
                      {MODULE_ENABLE_FLAG[r.key] ?? (MODULE_KIND[r.key] === "commercial" ? "Approval required" : "Automatic on valid content")}
                    </td>
                  )}
                  <td className="py-2 pr-4 text-muted-foreground break-words">{r.reason}</td>
                  <td className="py-2 pr-4 text-muted-foreground break-words">{r.missing.join(", ") || "—"}</td>
                  {detailed && <td className="py-2 pr-4 text-muted-foreground break-words">{gateFor(r)}</td>}
                  {detailed && <td className="py-2 text-muted-foreground break-words">{r.active ? "—" : MODULE_ADVICE[r.key]}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">
            Visible to admins only. Public visitors never receive this panel.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModuleDiagnostics;
